<template>
  <div>
    <v-container class="page-title page-title--advanced">
      <v-row>
        <v-col class="page-title__title-box">
          <h4 class="page-title__title">
            {{ $t("heading.instance.advanced.git.title") }}
          </h4>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col cols="12" class="pb-2">
          <v-alert v-if="hostingUnsupported" type="warning" dense outlined class="mb-4">
            {{ $t("heading.instance.advanced.git.hostingUnsupported") }}
          </v-alert>

          <git-card-skeleton
            v-if="loading"
            :connected="instance.git_repository && instance.git_repository.connected === true"
          />

          <template v-else>
            <div
              v-if="connected && gitStatus.show_drift_banner"
              class="alert alert--warning alert--outlined alert--100 mb-4 git-drift-banner"
            >
              <div class="alert__icon-box">
                <v-icon color="warning" size="20">$alertwarning</v-icon>
              </div>
              <div class="alert__text-box alert__text-box--wide git-drift-banner__body">
                <div class="git-drift-banner__message">
                  <p class="mb-0 p-3 text-gray-darken-1">
                    {{ $t("heading.instance.advanced.git.drift.banner") }}
                  </p>
                  <p
                    v-if="driftDivergedHint"
                    class="mb-0 px-3 pb-3 text-gray-darken-1 text-body-2"
                  >
                    {{ driftDivergedHint }}
                  </p>
                </div>
                <div
                  v-if="instance.hasManageGitPrivilege()"
                  class="git-drift-banner__actions"
                >
                  <v-btn
                    v-if="!isRemoteAheadOfSite"
                    small
                    color="primary"
                    elevation="0"
                    :loading="pushSubmitting"
                    :disabled="gitSyncActionDisabled"
                    @click="pushRepository"
                  >
                    {{ $t("heading.instance.advanced.git.drift.push") }}
                  </v-btn>
                  <v-btn
                    v-else
                    small
                    color="primary"
                    elevation="0"
                    :loading="pullSubmitting"
                    :disabled="gitSyncActionDisabled"
                    @click="pullRepository"
                  >
                    {{ $t("heading.instance.advanced.git.drift.pullFirst") }}
                  </v-btn>
                  <v-btn
                    small
                    color="error"
                    elevation="0"
                    :loading="revertSubmitting"
                    :disabled="gitSyncActionDisabled"
                    @click="revertRepository"
                  >
                    {{ $t("heading.instance.advanced.git.drift.revert") }}
                  </v-btn>
                  <v-btn
                    small
                    text
                    :loading="ignoreDriftSubmitting"
                    :disabled="gitActionDisabled"
                    @click="ignoreDrift"
                  >
                    {{ $t("heading.instance.advanced.git.drift.ignore") }}
                  </v-btn>
                </div>
              </div>
            </div>

            <div
              v-if="showLocalRepoMissingBanner"
              class="alert alert--warning alert--outlined alert--100 mb-4"
            >
              <div class="alert__icon-box">
                <v-icon color="warning" size="20">$alertwarning</v-icon>
              </div>
              <div class="alert__text-box alert__text-box--wide">
                <div class="d-flex flex-wrap align-center justify-space-between pa-3">
                  <p class="mb-0 text-gray-darken-1">
                    {{ $t("heading.instance.advanced.git.localRepoMissing.banner") }}
                  </p>
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    small
                    color="primary"
                    elevation="0"
                    class="mt-2 mt-sm-0"
                    :loading="repairSubmitting"
                    :disabled="gitActionDisabled"
                    @click="repairRepository"
                  >
                    {{ $t("heading.instance.advanced.git.localRepoMissing.repair") }}
                  </v-btn>
                </div>
              </div>
            </div>

            <div
              v-if="showSetupBanner"
              class="alert alert--info alert--outlined alert--100 mb-4"
            >
              <div class="alert__text-box alert__text-box--wide pa-3">
                <div class="d-flex flex-wrap align-center justify-space-between">
                  <p class="mb-0 text-gray-darken-1">
                    {{ $t("heading.instance.advanced.git.setupBanner.message") }}
                  </p>
                  <div v-if="instance.hasManageGitPrivilege()" class="d-flex flex-wrap align-center mt-2 mt-sm-0">
                    <span
                      v-if="setupBannerChecking"
                      class="gray--text text--darken-1 text-body-2"
                    >
                      {{ $t("heading.instance.advanced.git.setupBanner.checking") }}
                    </span>
                    <template v-else-if="setupBannerRemoteReady">
                      <v-btn
                        v-if="setupBannerShowPull"
                        small
                        color="primary"
                        elevation="0"
                        class="mr-2"
                        :loading="pullSubmitting"
                        :disabled="gitSyncActionDisabled"
                        @click="pullRepository"
                      >
                        {{ setupBannerPullLabel }}
                      </v-btn>
                      <v-btn
                        v-else-if="setupBannerShowPush"
                        small
                        color="primary"
                        elevation="0"
                        :loading="pushSubmitting"
                        :disabled="gitSyncActionDisabled"
                        @click="pushRepository"
                      >
                        {{ $t("heading.instance.advanced.git.setupBanner.push") }}
                      </v-btn>
                    </template>
                  </div>
                </div>
                <p
                  v-if="setupBannerDivergedHint"
                  class="mb-0 mt-3 gray--text text--darken-1 text-body-2"
                >
                  {{ setupBannerDivergedHint }}
                </p>
                <p
                  v-if="setupBannerSameBranchWarning"
                  class="mb-0 mt-3 warning--text text-body-2"
                >
                  {{ setupBannerSameBranchWarning }}
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    text
                    x-small
                    color="warning"
                    class="px-1 ml-1"
                    :disabled="gitSyncActionDisabled"
                    @click="showChangeBranchModal"
                  >
                    {{ $t("heading.instance.advanced.git.setupBanner.changeBranch") }}
                  </v-btn>
                </p>
              </div>
            </div>

            <!-- Connection card -->
            <v-card elevation="4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.connect.title") }}</h6>
                  <p class="mb-0 mt-1">
                    <template v-if="hostingUnsupported">
                      {{ $t("heading.instance.advanced.git.hostingUnsupported") }}
                    </template>
                    <template v-else>
                      {{ $t("heading.instance.advanced.git.connect.subtitle") }}
                    </template>
                  </p>
                </div>

                <div class="d-flex align-center">
                  <v-btn
                    v-if="connected"
                    color="error"
                    elevation="0"
                    :loading="submitting"
                    :disabled="gitActionDisabled"
                    @click="showDisconnectModal"
                  >
                    {{ $t("button.disconnect.default") }}
                  </v-btn>

                  <v-btn
                    v-else-if="instance.hasManageGitPrivilege()"
                    color="primary"
                    elevation="0"
                    :loading="submitting"
                    :disabled="hostingUnsupported || gitActionDisabled || !isConnectFormValid"
                    @click="connectRepository"
                  >
                    {{ $t("heading.instance.advanced.git.connect.button") }}
                  </v-btn>
                </div>
              </v-card-text>

              <v-divider class="mt-5"/>

              <v-card-text>
                <template v-if="connected">
                  <div class="mb-2">
                    <span class="gray--text text--darken-1">{{ $t("heading.instance.advanced.git.connected.repo") }}</span>
                    <a
                      v-if="connectedRepositoryHref"
                      :href="connectedRepositoryHref"
                      target="_blank"
                      rel="noopener"
                      class="ml-2 instance-domain-link"
                    >{{ gitStatus.repo_url }}</a>
                    <span v-else class="ml-2">{{ gitStatus.repo_url }}</span>
                  </div>

                  <div
                    v-if="connectionHealthMessage && connectionHealthAlertType !== 'success'"
                    :class="[
                      'alert',
                      'alert--outlined',
                      'alert--100',
                      'mb-2',
                      'mt-5',
                      connectionHealthAlertType === 'error' ? 'alert--error' : 'alert--warning',
                    ]"
                    style="padding: 10px 16px"
                  >
                    <div class="alert__icon-box">
                      <v-icon :color="connectionHealthAlertType" size="20">$alertwarning</v-icon>
                    </div>
                    <div class="alert__text-box alert__text-box--wide">
                      <p class="mb-0 p-3 text-gray-darken-1">
                        {{ connectionHealthMessage }}
                      </p>
                    </div>
                  </div>

                  <template v-if="showTokenUpdateForm">
                    <v-text-field
                      v-model="credentialsToken"
                      outlined
                      dense
                      hide-details
                      :label="$t('heading.instance.advanced.git.connection.tokenLabel')"
                      :type="showCredentialsToken ? 'text' : 'password'"
                      :disabled="gitActionDisabled"
                      class="mb-0"
                    >
                      <template v-slot:append>
                        <v-btn
                          x-small
                          icon
                          :disabled="gitActionDisabled"
                          @click="showCredentialsToken = !showCredentialsToken"
                        >
                          <v-icon size="18" color="gray">
                            {{ showCredentialsToken ? "$eyeclosed" : "$eye" }}
                          </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                    <form-field-doc-hint
                      class="mt-1 mb-2"
                      :link-text="$t('message.link.documentation.howToCreate')"
                    />
                    <v-btn
                      color="primary"
                      elevation="0"
                      small
                      :loading="credentialsSubmitting"
                      :disabled="gitActionDisabled || !(credentialsToken || '').trim()"
                      @click="updateGitCredentials"
                    >
                      {{ $t("heading.instance.advanced.git.connection.updateToken") }}
                    </v-btn>
                  </template>
                  <v-btn
                    v-else-if="connectionHealthIsError && connectionHealthReason === 'token' && instance.hasManageGitPrivilege()"
                    text
                    x-small
                    class="px-0"
                    :disabled="gitActionDisabled"
                    @click="showTokenUpdate = true"
                  >
                    {{ $t("heading.instance.advanced.git.connection.updateToken") }}
                  </v-btn>
                </template>

                <template v-else-if="!hostingUnsupported && !connected">
                  <v-btn-toggle
                    v-if="canReuseConnection"
                    v-model="showManualConnectForm"
                    mandatory
                    dense
                    class="git-connect-mode-toggle mb-4"
                  >
                    <v-btn :value="false" small :disabled="gitActionDisabled">
                      {{ $t("heading.instance.advanced.git.repoMode.useRelated") }}
                    </v-btn>
                    <v-btn :value="true" small :disabled="gitActionDisabled">
                      {{ $t("heading.instance.advanced.git.repoMode.useDifferent") }}
                    </v-btn>
                  </v-btn-toggle>

                  <template v-if="canReuseConnection && !showManualConnectForm">
                    <p class="mb-2 gray--text text--darken-1">
                      {{ $t("heading.instance.advanced.git.repoMode.reuseHint") }}
                    </p>
                    <div class="mb-2">
                      <span class="gray--text text--darken-1">{{ $t("heading.instance.advanced.git.connected.repo") }}</span>
                      <a
                        v-if="pairRepositoryHref"
                        :href="pairRepositoryHref"
                        target="_blank"
                        rel="noopener"
                        class="ml-2 font-weight-medium instance-domain-link"
                      >{{ pairRepoUrl }}</a>
                      <span v-else class="ml-2 font-weight-medium">{{ pairRepoUrl }}</span>
                    </div>
                    <p
                      v-if="instance.parentInstance"
                      class="mb-0 caption gray--text text--darken-1"
                    >
                      <template v-if="pairBranchLabel">
                        {{ $t("heading.instance.advanced.git.repoMode.stagingBranchHint", { branch: pairBranchLabel }) }}
                      </template>
                      <template v-else>
                        {{ $t("heading.instance.advanced.git.repoMode.stagingBranchHintDefault") }}
                      </template>
                    </p>
                  </template>

                  <template v-else>
                    <v-text-field
                      v-model="connectRepoUrl"
                      outlined
                      dense
                      :label="$t('heading.instance.advanced.git.connect.repoUrl')"
                      :disabled="gitActionDisabled"
                      class="mb-2"
                    />

                    <div class="mb-2 d-flex align-center justify-space-between">
                      <span class="font-weight-medium gray--text text--darken-1">
                        {{ $t("heading.instance.advanced.git.connect.privateRepo") }}
                      </span>
                      <v-switch v-model="connectIsPrivate" inset hide-details class="ma-0" :disabled="gitActionDisabled" />
                    </div>

                    <v-expand-transition>
                      <div v-if="connectIsPrivate" class="mb-2">
                        <v-text-field
                          v-model="connectPatToken"
                          outlined
                          dense
                          hide-details
                          :label="$t('heading.instance.advanced.git.connect.token')"
                          :type="showConnectToken ? 'text' : 'password'"
                          :disabled="gitActionDisabled"
                          class="mb-0"
                        >
                          <template v-slot:append>
                            <v-btn
                              x-small
                              icon
                              :disabled="gitActionDisabled"
                              @click="showConnectToken = !showConnectToken"
                            >
                              <v-icon size="18" color="gray">
                                {{ showConnectToken ? "$eyeclosed" : "$eye" }}
                              </v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                        <form-field-doc-hint
                          class="mt-1"
                          :hint="$t('heading.instance.advanced.git.connect.tokenHint')"
                          :link-text="$t('message.link.documentation.howToCreate')"
                          :doc-url="`https://panelalpha.com/documentation/multi-server/client-area-instances/advanced-settings/#provider-configuration`"
                        />
                      </div>
                    </v-expand-transition>
                  </template>
                </template>
              </v-card-text>
            </v-card>

            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.branch.title") }}</h6>
                  <p class="mb-0 mt-1">
                    {{ $t("heading.instance.advanced.git.branch.subtitle") }}
                  </p>
                </div>
                <div class="d-flex align-center">
                  <span class="p-3 d-flex align-center mx-4 gray--text text--darken-2">
                    {{
                      $t("heading.instance.advanced.git.branch.status", {
                        branch: gitStatus.branch || "—",
                      })
                    }}
                  </span>
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    elevation="0"
                    class="mt-0 pt-0 v-btn--default"
                    :disabled="gitSyncActionDisabled"
                    @click="showChangeBranchModal"
                  >
                    <span class="font-weight-bold">{{ $t("button.manage") }}</span>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.lastCommit.title") }}</h6>
                  <p class="mb-0 mt-1">
                    {{ $t("heading.instance.advanced.git.lastCommit.subtitle") }}
                  </p>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    elevation="0"
                    class="mt-0 pt-0 v-btn--default"
                    :loading="checkSubmitting"
                    :disabled="gitActionDisabled"
                    @click="checkForUpdates"
                  >
                    <span class="font-weight-bold">{{ $t("heading.instance.advanced.git.lastCommit.checkUpdates") }}</span>
                  </v-btn>
                </div>
              </v-card-text>

              <v-divider class="mt-5"/>

              <v-card-text class="git-commit-details">
                <template v-if="hasLastCommit">
                  <v-row dense class="git-kv-row">
                    <v-col cols="12" sm="3" class="gray--text text--darken-1">
                      {{ $t("heading.instance.advanced.git.lastCommit.hash") }}
                    </v-col>
                    <v-col cols="12" sm="9" class="d-flex align-center">
                      <v-tooltip
                        v-if="gitStatus.last_deployed_commit_hash"
                        open-delay="150"
                        bottom
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="font-weight-medium">
                            {{ lastCommitShortHash }}
                          </span>
                        </template>
                        <span>{{ gitStatus.last_deployed_commit_hash }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="gitStatus.last_deployed_commit_message"
                    dense
                    class="git-kv-row"
                  >
                    <v-col cols="12" sm="3" class="gray--text text--darken-1">
                      {{ $t("heading.instance.advanced.git.lastCommit.message") }}
                    </v-col>
                    <v-col cols="12" sm="9" class="text-break">
                      {{ gitStatus.last_deployed_commit_message }}
                    </v-col>
                  </v-row>
                  <v-row v-if="lastCommitDeployedAt" dense class="git-kv-row">
                    <v-col cols="12" sm="3" class="gray--text text--darken-1">
                      {{ $t("heading.instance.advanced.git.lastCommit.date") }}
                    </v-col>
                    <v-col cols="12" sm="9">
                      {{ lastCommitDeployedAt }}
                    </v-col>
                  </v-row>
                </template>
                <p v-else class="mb-0 gray--text text--darken-1">
                  {{ $t("heading.instance.advanced.git.lastCommit.empty") }}
                </p>
              </v-card-text>
            </v-card>

            <!-- AUTO DEPLOY CARD -->
            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="text--gray-darken2">
                <div class="d-flex justify-space-between flex-wrap v-card--mobile">
                  <div class="mb-2 mb-sm-0">
                    <h6>{{ $t("heading.instance.advanced.git.autoDeploy.title") }}</h6>
                    <p class="mb-0 mt-1">
                      {{
                        $t("heading.instance.advanced.git.autoDeploy.subtitle", {
                          branch: gitStatus.branch || "—",
                        })
                      }}
                    </p>
                  </div>
                  <div v-if="instance.hasManageGitPrivilege()" class="d-flex align-center">
                    <v-switch
                      :input-value="gitStatus.auto_deploy_enabled"
                      inset
                      hide-details
                      class="ma-0"
                      :loading="autoDeploySubmitting"
                      :disabled="gitActionDisabled || isAutoDeployInProgress"
                      @change="onAutoDeployToggle"
                    />
                  </div>
                </div>

                <p
                  v-if="isAutoDeployInProgress"
                  class="mb-0 mt-3 info--text text-body-2"
                >
                  {{ $t("heading.instance.advanced.git.autoDeploy.inProgress") }}
                </p>
              </v-card-text>
            </v-card>

            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.pull.card.title") }}</h6>
                  <p class="mb-0 mt-1">
                    {{ $t("heading.instance.advanced.git.pull.card.subtitle") }}
                  </p>
                  <p
                    v-if="showPullPushToLiveHint"
                    class="mb-0 mt-2 gray--text text--darken-1 text-body-2"
                  >
                    {{ $t("heading.instance.advanced.git.pull.pushToLiveHint") }}
                  </p>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    color="primary"
                    elevation="0"
                    :loading="pullSubmitting"
                    :disabled="gitSyncActionDisabled"
                    @click="pullRepository"
                  >
                    {{ $t("button.git.pull") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.push.card.title") }}</h6>
                  <p class="mb-0 mt-1">
                    {{ $t("heading.instance.advanced.git.push.card.subtitle") }}
                  </p>
                  <p
                    v-if="isRemoteAheadOfSite"
                    class="mb-0 mt-2 warning--text text-body-2"
                  >
                    {{ $t("heading.instance.advanced.git.push.pullFirstHint") }}
                  </p>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    v-if="instance.hasManageGitPrivilege()"
                    color="primary"
                    elevation="0"
                    :loading="pushSubmitting"
                    :disabled="gitSyncActionDisabled || isRemoteAheadOfSite"
                    @click="pushRepository"
                  >
                    {{ $t("button.git.push") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-card v-if="connected" elevation="4" class="mt-4">
              <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
                <div>
                  <h6>{{ $t("heading.instance.advanced.git.logs.title") }}</h6>
                  <p class="mb-0 mt-1">
                    {{ $t("heading.instance.advanced.git.logs.subtitle") }}
                  </p>
                </div>
              </v-card-text>

              <v-divider class="mt-5"/>

              <v-card-text>
                <git-logs-table
                  :items="gitLogs"
                  :items-loading="gitLogsLoading"
                  :page="gitLogsPage"
                  :items-per-page="gitLogsItemsPerPage"
                  :number-of-pages="gitLogsNumberOfPages"
                  :possible-items-per-page="[[5, 15, 50, 100]]"
                  :list-style="0"
                  :per-page-label="$t('table.footer.log')"
                  :items-total="gitLogsTotal"
                  @update:page="changeGitLogsPage"
                  @update:itemsPerPage="changeGitLogsPerPage"
                />
              </v-card-text>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
      transition="custom-dialog-transition"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()" />
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
        ref="modal"
      />
    </v-dialog>

  </div>
</template>

<script>
import Api from "@/apis/Api";
import GitCardSkeleton from "@/components/cards/GitCardSkeleton.vue";
import GitLogsTable from "@/components/dataIterator/tables/GitLogsTable.vue";
import FormFieldDocHint from "@/components/FormFieldDocHint.vue";
import BasicModal from "@/components/modal/BasicModal.vue";
import ActionModalMixin from "@/mixins/ActionModalMixin";
import GitActionsMixin from "@/mixins/GitActionsMixin";
import moment from "moment";

export default {
  components: {
    GitCardSkeleton,
    GitLogsTable,
    FormFieldDocHint,
    BasicModal,
  },
  mixins: [ActionModalMixin, GitActionsMixin],
  props: {
    instance: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      submitting: false,
      pullSubmitting: false,
      pushSubmitting: false,
      revertSubmitting: false,
      ignoreDriftSubmitting: false,
      checkSubmitting: false,
      autoDeploySubmitting: false,
      autoDeployPollTimer: null,
      credentialsSubmitting: false,
      repairSubmitting: false,
      credentialsToken: "",
      showTokenUpdate: false,
      showCredentialsToken: false,
      gitLogs: [],
      gitLogsLoading: false,
      gitLogsPage: 1,
      gitLogsItemsPerPage: 5,
      gitLogsTotal: 0,
      gitLogsLastPage: 1,
      gitStatus: {
        connected: false,
        repo_url: null,
        branch: null,
        pair_branch: null,
        last_deployed_commit_hash: null,
        last_deployed_commit_message: null,
        last_deployed_commit_at: null,
        has_synced_once: false,
        drift_status: null,
        commits_behind: null,
        commits_ahead: null,
        remote_branch_exists: null,
        local_repository_exists: null,
        show_drift_banner: false,
        connection_health: { status: "ok", reason: null },
        auto_deploy_enabled: false,
        webhook_registered: false,
        async_status: { git_attach: null, git_pull: null, git_auto_deploy: null },
      },
      setupBannerChecked: false,
      setupBannerChecking: false,
      hostingUnsupported: false,
      showManualConnectForm: false,
      connectRepoUrl: "",
      connectIsPrivate: false,
      connectPatToken: "",
      showConnectToken: false,
    };
  },
  computed: {
    connected() {
      return this.gitStatus.connected === true;
    },
    canReuseConnection() {
      return this.pair?.git_repository?.connected === true;
    },
    pairRepoUrl() {
      return this.pair?.git_repository?.repo_url || "";
    },
    connectedRepositoryHref() {
      return this.toRepositoryHref(this.gitStatus.repo_url);
    },
    pairRepositoryHref() {
      return this.toRepositoryHref(this.pairRepoUrl);
    },
    pairBranchLabel() {
      return (this.pair?.git_repository?.branch || "").trim() || null;
    },
    normalizedConnectRepoUrl() {
      return (this.connectRepoUrl || "").trim();
    },
    isConnectFormValid() {
      if (this.canReuseConnection && !this.showManualConnectForm) {
        return true;
      }
      if (!this.normalizedConnectRepoUrl) {
        return false;
      }
      return !this.connectIsPrivate || !!(this.connectPatToken || "").trim();
    },
    gitCommitsBehind() {
      return Number(this.gitStatus.commits_behind || 0);
    },
    gitCommitsAhead() {
      return Number(this.gitStatus.commits_ahead || 0);
    },
    isRemoteAheadOfSite() {
      return this.hasRemoteStatus && this.gitCommitsBehind > 0;
    },
    gitActionDisabled() {
      return (
        this.submitting ||
        this.pullSubmitting ||
        this.pushSubmitting ||
        this.revertSubmitting ||
        this.repairSubmitting ||
        this.ignoreDriftSubmitting ||
        this.checkSubmitting ||
        this.autoDeploySubmitting ||
        this.credentialsSubmitting ||
        this.setupBannerChecking ||
        this.modalOptions?.submitting === true
      );
    },
    showLocalRepoMissingBanner() {
      return this.connected && this.gitStatus.local_repository_exists === false;
    },
    localRepoActionsDisabled() {
      return this.showLocalRepoMissingBanner;
    },
    gitSyncActionDisabled() {
      return this.gitActionDisabled || this.localRepoActionsDisabled;
    },
    isAutoDeployInProgress() {
      return (this.gitStatus.async_status?.git_auto_deploy || "") === "in_progress";
    },
    showSetupBanner() {
      return this.connected && this.gitStatus.has_synced_once !== true;
    },
    setupBannerRemoteReady() {
      return this.hasRemoteStatus;
    },
    setupBannerRecommendPush() {
      if (!this.setupBannerRemoteReady) {
        return false;
      }

      if (this.gitCommitsBehind > 0) {
        return false;
      }

      if (this.gitCommitsAhead > 0) {
        return true;
      }

      if (this.gitStatus.remote_branch_exists === false) {
        return true;
      }

      return this.gitStatus.drift_status === "dirty";
    },
    setupBannerShowPull() {
      return this.setupBannerRemoteReady && !this.setupBannerRecommendPush;
    },
    setupBannerShowPush() {
      return this.setupBannerRemoteReady && this.setupBannerRecommendPush;
    },
    setupBannerDivergedHint() {
      if (!this.setupBannerRemoteReady) {
        return null;
      }

      if (this.gitCommitsBehind > 0 && this.gitCommitsAhead > 0) {
        return this.$t("heading.instance.advanced.git.setupBanner.divergedHint");
      }

      return null;
    },
    setupBannerPullLabel() {
      const behind = Number(this.gitStatus.commits_behind || 0);
      if (behind > 0) {
        return this.$t("heading.instance.advanced.git.setupBanner.pullBehind", { count: behind });
      }
      return this.$t("heading.instance.advanced.git.setupBanner.pull");
    },
    setupBannerSameBranchWarning() {
      const pairBranch = (this.gitStatus.pair_branch || "").trim();
      const currentBranch = (this.gitStatus.branch || "").trim();

      if (!pairBranch || !currentBranch || pairBranch !== currentBranch) {
        return null;
      }

      if (this.instance.parentInstance) {
        return this.$t("heading.instance.advanced.git.setupBanner.sameBranchStaging", {
          branch: currentBranch,
        });
      }

      if (this.instance.subInstances?.length > 0) {
        return this.$t("heading.instance.advanced.git.setupBanner.sameBranchLive", {
          branch: currentBranch,
        });
      }

      return null;
    },
    hasLastCommit() {
      return !!(this.gitStatus.last_deployed_commit_hash || "").toString().trim();
    },
    lastCommitShortHash() {
      return this.shortCommitHash(this.gitStatus.last_deployed_commit_hash);
    },
    lastCommitDeployedAt() {
      const value = this.gitStatus.last_deployed_commit_at;
      if (!value) {
        return null;
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return `${date.format("YYYY-MM-DD HH:mm")} (${date.fromNow()})`;
    },
    gitLogsNumberOfPages() {
      return this.gitLogsLastPage || 1;
    },
    pair() {
      if (this.instance.parentInstance) {
        return this.instance.parentInstance;
      }

      if (this.instance.subInstances.length > 0) {
        return this.instance.subInstances[0];
      }

      return null;
    },
    connectionHealthIsError() {
      return (this.gitStatus.connection_health?.status || "ok") === "error";
    },
    connectionHealthReason() {
      return this.gitStatus.connection_health?.reason || null;
    },
    connectionHealthAlertType() {
      return this.connectionHealthIsError ? "error" : "success";
    },
    connectionHealthMessage() {
      if (!this.connected) {
        return null;
      }
      if (!this.connectionHealthIsError) {
        return this.$t("heading.instance.advanced.git.connection.ok");
      }
      const reason = this.connectionHealthReason;
      if (reason === "token") {
        return this.$t("heading.instance.advanced.git.connection.error.token");
      }
      if (reason === "network") {
        return this.$t("heading.instance.advanced.git.connection.error.network");
      }
      if (reason === "not_found") {
        return this.$t("heading.instance.advanced.git.connection.error.not_found");
      }
      if (reason === "local_repo_missing") {
        return this.$t("heading.instance.advanced.git.connection.error.localRepoMissing");
      }
      return this.$t("heading.instance.advanced.git.connection.error.unknown");
    },
    showTokenUpdateForm() {
      return this.showTokenUpdate && this.connectionHealthReason === "token";
    },
    hasRemoteStatus() {
      return (
        this.gitStatus.commits_behind !== null &&
        this.gitStatus.commits_behind !== undefined
      ) || (
        this.gitStatus.commits_ahead !== null &&
        this.gitStatus.commits_ahead !== undefined
      );
    },
    driftDivergedHint() {
      if (!this.isRemoteAheadOfSite) {
        return null;
      }

      if (this.gitCommitsBehind > 0 && this.gitCommitsAhead > 0) {
        return this.$t("heading.instance.advanced.git.drift.divergedHint");
      }

      return this.$t("heading.instance.advanced.git.push.pullFirstHint");
    },
    showPullPushToLiveHint() {
      return !!this.instance.parentInstance;
    },
  },
  mounted() {
    this.bootstrapGit();
  },
  beforeDestroy() {
    this.stopAutoDeployPolling();
  },
  methods: {
    async bootstrapGit() {
      await this.fetchGitStatus();

      if (!this.connected) {
        return;
      }

      await this.refreshGitStatus();
    },
    shortCommitHash(value) {
      const hash = (value || "").toString().trim();
      if (!hash) {
        return "";
      }
      return hash.length > 10 ? `${hash.slice(0, 7)}…${hash.slice(-3)}` : hash;
    },
    toRepositoryHref(url) {
      const value = (url || "").trim();
      if (!value) {
        return null;
      }

      if (/^https?:\/\//i.test(value)) {
        return value.replace(/\.git$/, "");
      }

      const sshMatch = value.match(/^git@([^:]+):(.+?)(?:\.git)?$/);
      if (sshMatch) {
        return `https://${sshMatch[1]}/${sshMatch[2]}`;
      }

      const sshUrlMatch = value.match(/^ssh:\/\/git@([^/]+)\/(.+?)(?:\.git)?$/);
      if (sshUrlMatch) {
        return `https://${sshUrlMatch[1]}/${sshUrlMatch[2]}`;
      }

      return null;
    },
    fetchGitLogs() {
      if (!this.connected) {
        return Promise.resolve();
      }

      this.gitLogsLoading = true;
      const url =
        `?page=${this.gitLogsPage}` +
        `&per_page=${this.gitLogsItemsPerPage}` +
        `&sort_by=date&sort_dir=desc`;

      return Api.get(`/instances/${this.instance.id}/git/logs${url}`)
        .then((response) => {
          this.gitLogs = response.data.data || [];
          const meta = response.data.meta || {};
          this.gitLogsTotal = meta.total || 0;
          this.gitLogsLastPage = meta.last_page || 1;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.gitLogsLoading = false;
        });
    },
    changeGitLogsPage(page) {
      this.gitLogsPage = page;
      this.fetchGitLogs();
    },
    changeGitLogsPerPage(perPage) {
      this.gitLogsPage = 1;
      this.gitLogsItemsPerPage = perPage;
      this.fetchGitLogs();
    },
    fetchGitStatus({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
      }
      return Api.get(`/instances/${this.instance.id}/git`)
        .then((response) => {
          this.gitStatus = response.data.data;
          this.hostingUnsupported = false;

          if (this.isAutoDeployInProgress) {
            this.startAutoDeployPolling();
          } else {
            this.stopAutoDeployPolling();
          }

          if (!this.connected) {
            this.gitLogs = [];
            this.gitLogsTotal = 0;
            this.gitLogsPage = 1;
            this.gitLogsLastPage = 1;
          } else {
            this.fetchGitLogs();
            if (this.showSetupBanner && !this.setupBannerChecked) {
              this.setupBannerChecked = true;
              this.setupBannerChecking = true;
              this.refreshGitStatus()
                .catch(() => {})
                .finally(() => {
                  this.setupBannerChecking = false;
                });
            }
          }
        })
        .catch((error) => {
          const message = Api.getErrorMessage(error);
          if (message && message.toLowerCase().includes("not supported")) {
            this.hostingUnsupported = true;
          } else {
            this.$store.dispatch("addAlert", {
              success: false,
              errorMessage: message,
            });
          }
        })
        .finally(() => {
          if (!silent) {
            this.loading = false;
          }
        });
    },
    onAutoDeployToggle(enabled) {
      this.updateAutoDeploy(enabled);
    },
    startAutoDeployPolling() {
      if (this.autoDeployPollTimer) {
        return;
      }

      this.autoDeployPollTimer = setInterval(() => {
        this.fetchGitStatus({ silent: true }).then(() => {
          if (!this.isAutoDeployInProgress) {
            this.fetchGitLogs();
          }
        });
      }, 5000);
    },
    stopAutoDeployPolling() {
      if (!this.autoDeployPollTimer) {
        return;
      }

      clearInterval(this.autoDeployPollTimer);
      this.autoDeployPollTimer = null;
    },
    buildConnectPayload() {
      if (this.canReuseConnection && !this.showManualConnectForm) {
        return {
          git_repository_id: this.pair.git_repository.id,
        };
      }

      return {
        repo_url: this.normalizedConnectRepoUrl,
        auth_type: this.connectIsPrivate ? "pat" : null,
        token: this.connectIsPrivate ? (this.connectPatToken || "").trim() : null,
      };
    },
    async connectRepository() {
      if (this.submitting || !this.isConnectFormValid) {
        return;
      }

      this.submitting = true;

      try {
        await Api.post(`/instances/${this.instance.id}/git/connect`, this.buildConnectPayload());
        this.connectRepoUrl = "";
        this.connectIsPrivate = false;
        this.connectPatToken = "";
        this.showManualConnectForm = false;
        this.setupBannerChecked = false;
        await this.fetchGitStatus({ silent: true });
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.connect.success"),
        });
      } catch (error) {
        const response = error.response;
        const connectedDespiteError =
          response?.status === 422 && response?.data?.connected === true;

        if (connectedDespiteError) {
          this.connectRepoUrl = "";
          this.connectIsPrivate = false;
          this.connectPatToken = "";
          this.showManualConnectForm = false;
          this.setupBannerChecked = false;
          await this.fetchGitStatus({ silent: true });

          const syncMessage = (response.data.message || "").trim();
          await this.$store.dispatch("addAlert", {
            success: true,
            successMessage: syncMessage
              ? this.$t("heading.instance.advanced.git.connect.partialSyncFailed", {
                  message: syncMessage,
                })
              : this.$t("notification.instance.git.connect.success"),
          });
        } else {
          await this.$store.dispatch("addAlert", {
            success: false,
            errorMessage:
              Api.getErrorMessage(error) ||
              this.$t("heading.instance.advanced.git.connect.failed"),
          });
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.git-connect-mode-toggle {
  display: flex;
  width: 100%;
  border: 1px solid var(--v-gray-lighten2);
  border-radius: 8px;
  overflow: hidden;

  ::v-deep .v-btn {
    flex: 1 1 0;
    min-width: 0;
    height: 38px;
    border: none;
    border-radius: 0;
    text-transform: none;
    letter-spacing: normal;

    &:not(:last-child) {
      border-right: 1px solid var(--v-gray-lighten2);
    }
  }
}

.git-drift-banner {
  align-items: center;
  padding: 12px 16px;

  .alert__icon-box {
    align-self: center;
  }

  &__body {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 0 0 8px;
    width: 100%;
  }

  &__message {
    flex: 1 1 200px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  @media (max-width: 767px) {
    &__body {
      flex-direction: column;
      align-items: stretch;
    }

    &__actions .v-btn {
      flex: 1 1 auto;
    }
  }
}

.v-card--mobile {
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;

    > :last-child {
      margin-top: 12px;
      width: 100%;
      justify-content: flex-start;
    }

    .v-btn {
      width: 100%;
    }
  }
}

.git-commit-details {
  $git-kv-gap: 4px;

  .git-kv-row + .git-kv-row {
    margin-top: 0;
  }

  .git-kv-row ::v-deep > .col {
    padding-top: $git-kv-gap / 2;
    padding-bottom: $git-kv-gap / 2;
    line-height: 1.35;
    min-height: 0;
  }
}
</style>
